import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Mail, Megaphone, MousePointerClick, Send, TrendingUp, XCircle } from 'lucide-react'

import type { MarketingOverview } from '@/types/marketing'
import { useMarketingOverview } from '@/features/marketing/hooks'
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
} from '@/features/dashboard/components/chart-theme'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

function monthLabel(month: string) {
  const [y, m] = month.split('-')
  const date = new Date(Number(y), Number(m) - 1, 1)
  return date.toLocaleDateString(undefined, { month: 'short' })
}

export function OverviewTab() {
  const { data, isLoading } = useMarketingOverview(12)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    )
  }

  const totals = data?.totals
  const deliveryRate =
    totals && totals.recipients > 0 ? Math.round((totals.sent / totals.recipients) * 100) : 0

  const chartData =
    data?.months.map((m) => ({
      month: monthLabel(m.month),
      sends: m.sends,
      opens: m.opens,
      clicks: m.clicks,
      spend: Math.round(m.spend_cents / 100),
    })) ?? []

  return (
    <div className="space-y-6">
      <StatCards totals={totals} deliveryRate={deliveryRate} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4" /> Sends, engagement &amp; spend by month
          </CardTitle>
          <CardDescription>
            Campaign volume (sends, opens, clicks) against the marketing spend recorded in the
            ledger. Spend comes from finance entries categorized as marketing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.every((m) => m.sends === 0 && m.spend === 0) ? (
            <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
              No campaign or spend data yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={chartData}>
                <CartesianGrid {...CHART_GRID_PROPS} />
                <XAxis dataKey="month" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis
                  yAxisId="left"
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  {...CHART_TOOLTIP_PROPS}
                  formatter={(value, name) =>
                    name === 'spend' ? `$${Number(value).toLocaleString()}` : Number(value)
                  }
                />
                <Bar
                  yAxisId="left"
                  dataKey="sends"
                  name="sends"
                  fill="hsl(var(--chart-1))"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="opens"
                  name="opens"
                  fill="hsl(var(--chart-2))"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="clicks"
                  name="clicks"
                  fill="hsl(var(--chart-3))"
                  radius={[3, 3, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="spend"
                  name="spend"
                  stroke="hsl(var(--chart-4))"
                  dot={false}
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatCards({
  totals,
  deliveryRate,
}: {
  totals: MarketingOverview['totals'] | undefined
  deliveryRate: number
}) {
  const cards = [
    { label: 'Campaigns', value: totals?.campaigns ?? 0, icon: Megaphone },
    { label: 'Delivered', value: totals?.sent ?? 0, icon: Send },
    { label: 'Delivery rate', value: `${deliveryRate}%`, icon: Mail },
    { label: 'Opened', value: totals?.opened ?? 0, icon: Mail },
    { label: 'Clicked', value: totals?.clicked ?? 0, icon: MousePointerClick },
    { label: 'Failed', value: totals?.failed ?? 0, icon: XCircle },
  ]
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map(({ label, value, icon: Icon }) => (
        <Card key={label}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <div className="mt-1 text-2xl font-bold">{value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
